/**
 * CouponCard - Presentation Component
 *
 * Displays a single coupon card matching the mobile VoucherCard design:
 * ticket-style with perforated left border, brand image, coupon info,
 * "Điều kiện" and "Lấy mã" action links.
 *
 * @see UI Core: mario-nextjs-react-ui-core/organisms/voucher-card/
 */

'use client';

import React, { useCallback, useMemo } from 'react';
import { cn, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import type { CouponCardProps } from './coupon-card.types';
import { getCouponImageUri } from '@kidslabs/module-flash-sale';
import styles from './coupon-card.module.css';

/**
 * Strip HTML tags from a string, returning plain text.
 */
const stripHtml = (html: string): string => {
    if (typeof document !== 'undefined') {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent ?? tmp.innerText ?? '';
    }
    return html.replace(/<[^>]*>/g, '');
};

/**
 * Format date to DD/MM/YYYY
 */
const formatExpiryDate = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
        return dateStr;
    }
};

export const CouponCard: React.FC<CouponCardProps> = React.memo(
    ({
        coupon,
        variant = 'voucher',
        isInWallet = false,
        isAdding = false,
        actionLabel,
        isSelectable = false,
        isSelected = false,
        isDisabled = false,
        ineligibilityReasons,
        applicableAmount,
        onAction,
        onShowCondition,
        onSelect,
        className,
    }) => {
        const imageUri = useMemo(() => getCouponImageUri(coupon), [coupon]);

        const expiryText = useMemo(() => {
            const from = coupon.salesrule?.from_date;
            const to = coupon.salesrule?.to_date;
            if (!to) return '';
            if (from) return `${formatExpiryDate(from)} - ${formatExpiryDate(to)}`;
            return formatExpiryDate(to);
        }, [coupon.salesrule?.from_date, coupon.salesrule?.to_date]);

        const conditionText = useMemo(() => {
            if (!coupon.condition) return '';
            return stripHtml(coupon.condition);
        }, [coupon.condition]);

        const instructionText = useMemo(() => {
            if (!coupon.instruction) return '';
            return stripHtml(coupon.instruction);
        }, [coupon.instruction]);

        const buttonLabel = useMemo(() => {
            if (actionLabel) return actionLabel;
            if (isInWallet && variant !== 'eWalletVoucher') return 'Đã lấy';
            if (variant === 'eWalletVoucher') return 'Xem chi tiết';
            return 'Lấy mã';
        }, [actionLabel, isInWallet, variant]);

        const isActionDisabled = useMemo(
            () => isAdding || (isInWallet && variant !== 'eWalletVoucher') || isDisabled,
            [isAdding, isInWallet, variant, isDisabled]
        );

        const handleCardClick = useCallback(() => {
            if (isSelectable && onSelect && !isDisabled) {
                onSelect();
            }
        }, [isSelectable, onSelect, isDisabled]);

        const handleActionClick = useCallback(
            (event: React.MouseEvent) => {
                event.stopPropagation();
                if (!isActionDisabled && onAction) {
                    onAction();
                }
            },
            [isActionDisabled, onAction]
        );

        const handleConditionClick = useCallback(
            (event: React.MouseEvent) => {
                event.stopPropagation();
                onShowCondition?.();
            },
            [onShowCondition]
        );

        return (
            <div
                className={cn(
                    styles.card,
                    isDisabled && styles.cardDisabled,
                    isSelectable && styles.cardSelectable,
                    isSelected && styles.cardSelected,
                    className
                )}
                onClick={handleCardClick}
                role={isSelectable ? 'radio' : 'article'}
                aria-checked={isSelectable ? isSelected : undefined}
                aria-disabled={isDisabled}
                tabIndex={isSelectable ? 0 : undefined}
            >
                {/* Perforated left border (dotted color line) */}
                <div
                    className={styles.leftDots}
                    style={{
                        backgroundImage: `radial-gradient(circle, ${coupon.border_color ?? 'var(--color-primary-100)'} 50%, transparent 51%)`,
                    }}
                />

                {/* Circular cutouts (top/bottom notches) */}
                <div className={styles.cutoutTop} />
                <div className={styles.cutoutBottom} />

                {/* Inner card content */}
                <div className={styles.cardInner}>
                    {/* Left - Brand Image */}
                    <div
                        className={styles.imageSection}
                        style={{
                            backgroundColor: coupon.background_color ?? undefined,
                        }}
                    >
                        {imageUri ? (
                            <img
                                src={imageUri}
                                alt={coupon.name ?? 'Coupon'}
                                className={styles.brandImage}
                                loading="lazy"
                            />
                        ) : (
                            <div className={styles.imagePlaceholder}>
                                <Text size="small" weight="bold" className={styles.placeholderText}>
                                    {coupon.name?.charAt(0) ?? 'C'}
                                </Text>
                            </div>
                        )}
                    </div>

                    {/* Dashed separator */}
                    <div className={styles.dashedSeparator} />

                    {/* Right - Info */}
                    <div className={styles.infoSection}>
                        <Text size="medium" weight="bold" className={styles.couponName}>
                            {coupon.name}
                        </Text>

                        {applicableAmount !== undefined && applicableAmount > 0 && (
                            <Text size="small" weight="bold" className={styles.applicableAmount}>
                                {`-${applicableAmount.toLocaleString('vi-VN')}đ`}
                            </Text>
                        )}

                        {conditionText && (
                            <Text size="small" className={styles.couponCondition}>
                                {conditionText}
                            </Text>
                        )}

                        {ineligibilityReasons && ineligibilityReasons.length > 0 && (
                            <div className={styles.ineligibilityBadge}>
                                <Text size="xsmall" className={styles.ineligibilityText}>
                                    {ineligibilityReasons[0]}
                                </Text>
                            </div>
                        )}

                        {instructionText && (
                            <Text size="xsmall" className={styles.couponInstruction}>
                                {instructionText}
                            </Text>
                        )}

                        {/* Code + Expiry row */}
                        <div className={styles.metaRow}>
                            {coupon.salesrule?.code && (
                                <Text size="xsmall" weight="bold" className={styles.couponCode}>
                                    {coupon.salesrule.code}
                                </Text>
                            )}
                            {expiryText && (
                                <Text size="xsmall" className={styles.expiryDate}>
                                    HSD: {expiryText}
                                </Text>
                            )}
                        </div>

                        {/* Actions row */}
                        <div className={styles.actions}>
                            {onShowCondition && (
                                <button
                                    type="button"
                                    className={styles.conditionLink}
                                    onClick={handleConditionClick}
                                    aria-label="Xem điều kiện coupon"
                                >
                                    Điều kiện
                                </button>
                            )}

                            {!isSelectable && (
                                <button
                                    type="button"
                                    className={cn(styles.actionLink, isActionDisabled && styles.actionLinkDisabled)}
                                    onClick={handleActionClick}
                                    disabled={isActionDisabled}
                                    aria-label={buttonLabel}
                                >
                                    {isAdding ? <span className={styles.spinner} /> : buttonLabel}
                                </button>
                            )}

                            {isSelectable && (
                                <div
                                    className={cn(styles.radioIndicator, isSelected && styles.radioIndicatorSelected)}
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

CouponCard.displayName = 'CouponCard';
